import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { getColors } from '../styles/colors';
import cookieManagerData from '../data/components/cookie-manager.json';
import TextToSpeechPlayer from './TextToSpeechPlayer';
import * as S from '../styles/components/CookieManager.styles';

interface CookiePreferences {
  essential: boolean; // Immer true, kann nicht deaktiviert werden
  functional: boolean; // Spracheinstellungen, Barrierefreiheit
}

interface CookieMetadata {
  name: string;
  category: 'essential' | 'functional';
  description: string;
  expirationDays: number; // Wie viele Tage bis Ablauf
  lastAccepted?: string; // ISO-Datum wann zuletzt akzeptiert
  expiresAt?: string; // ISO-Datum wann abläuft
}

interface CookieConsentData {
  consentDate: string; // ISO-Datum wann Zustimmung gegeben wurde
  preferences: CookiePreferences;
  cookies: CookieMetadata[];
}

// Context für Cookie Manager
const CookieManagerContext = React.createContext<{
  openCookieSettings: () => void;
  areFunctionalCookiesAllowed: () => boolean;
} | null>(null);

export const useCookieManager = () => {
  const context = React.useContext(CookieManagerContext);
  if (!context) {
    throw new Error('useCookieManager must be used within CookieManagerProvider');
  }
  return context;
};

// Utility-Funktion zum Prüfen, ob funktionale Cookies erlaubt sind
export const areFunctionalCookiesAllowed = (): boolean => {
  const cookieConsent = localStorage.getItem('cookieConsent');
  if (!cookieConsent) {
    // Keine Zustimmung gegeben - Standard: erlauben (bis User entscheidet)
    return true;
  }
  const savedPrefs = localStorage.getItem('cookiePreferences');
  if (savedPrefs) {
    try {
      const prefs = JSON.parse(savedPrefs);
      return prefs.functional === true;
    } catch (e) {
      return true; // Bei Fehler: erlauben
    }
  }
  return true; // Standard: erlauben
};

// Provider Component
export const CookieManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    functional: true
  });

  const openCookieSettings = () => {
    setShowModal(true);
  };

  // Cookie-Metadaten definieren
  const cookieMetadata: CookieMetadata[] = [
    {
      name: 'cookieConsent',
      category: 'essential',
      description: 'Speichert, dass Sie eine Cookie-Entscheidung getroffen haben, um den Cookie-Banner nicht bei jedem Besuch anzuzeigen',
      expirationDays: 365 // 1 Jahr
    },
    {
      name: 'theme',
      category: 'essential',
      description: 'Speichert das Theme (hell/dunkel) für die Website',
      expirationDays: 365 // 1 Jahr
    },
    {
      name: 'siteLanguage',
      category: 'functional',
      description: 'Speichert Ihre bevorzugte Sprache (Deutsch/Englisch)',
      expirationDays: 365 // 1 Jahr
    },
    {
      name: 'accessibilitySettings',
      category: 'functional',
      description: 'Speichert Ihre Barrierefreiheitseinstellungen (Schriftgröße, Kontrast, Animationen, etc.)',
      expirationDays: 365 // 1 Jahr
    }
  ];

  // Berechne Ablaufdatum
  const calculateExpirationDate = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString();
  };

  // Wende Cookie-Präferenzen an
  const applyCookiePreferences = (prefs: CookiePreferences) => {
    if (!prefs.functional) {
      // Funktionale Cookies deaktivieren - lösche entsprechende localStorage-Einträge
      localStorage.removeItem('siteLanguage');
      localStorage.removeItem('accessibilitySettings');
      // Setze auf Standardwerte
      document.documentElement.lang = 'de';
      // Reset accessibility settings
      const root = document.documentElement;
      root.style.removeProperty('--accessibility-contrast');
      root.style.removeProperty('--accessibility-brightness');
      root.style.removeProperty('--accessibility-font-size');
      root.classList.remove('high-contrast-mode');
      root.classList.remove('reduce-motion');
      document.body.classList.remove('reduce-motion');
    }
  };

  // Speichere Cookie-Metadaten mit Timestamps
  const saveCookieConsent = (prefs: CookiePreferences) => {
    const now = new Date().toISOString();
    const cookiesWithMetadata = cookieMetadata.map(cookie => ({
      ...cookie,
      lastAccepted: now,
      expiresAt: calculateExpirationDate(cookie.expirationDays)
    }));

    const consentData: CookieConsentData = {
      consentDate: now,
      preferences: prefs,
      cookies: cookiesWithMetadata.filter(cookie => 
        cookie.category === 'essential' || 
        (cookie.category === 'functional' && prefs.functional)
      )
    };

    // cookieConsent wird immer gesetzt (essentiell)
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    localStorage.setItem('cookieMetadata', JSON.stringify(consentData));
  };

  // Prüfe ob Cookie-Zustimmung bereits gegeben wurde
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowBanner(true);
      const savedPrefs = localStorage.getItem('cookiePreferences');
      if (savedPrefs) {
        try {
          setPreferences(JSON.parse(savedPrefs));
        } catch (e) {
          console.error('Failed to parse cookie preferences:', e);
        }
      }
    } else {
      const savedPrefs = localStorage.getItem('cookiePreferences');
      if (savedPrefs) {
        try {
          const prefs = JSON.parse(savedPrefs);
          setPreferences(prefs);
          applyCookiePreferences(prefs);
        } catch (e) {
          console.error('Failed to parse cookie preferences:', e);
        }
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const newPrefs: CookiePreferences = {
      essential: true,
      functional: true
    };
    setPreferences(newPrefs);
    saveCookieConsent(newPrefs);
    setShowBanner(false);
    applyCookiePreferences(newPrefs);
  };

  const handleRejectAll = () => {
    const newPrefs: CookiePreferences = {
      essential: true,
      functional: false
    };
    setPreferences(newPrefs);
    saveCookieConsent(newPrefs);
    setShowBanner(false);
    applyCookiePreferences(newPrefs);
  };

  const handleSavePreferences = () => {
    saveCookieConsent(preferences);
    setShowModal(false);
    setShowBanner(false);
    applyCookiePreferences(preferences);
  };

  const handleFunctionalToggle = (checked: boolean) => {
    setPreferences(prev => ({
      ...prev,
      functional: checked
    }));
  };

  const areFunctionalCookiesAllowed = (): boolean => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      return true; // Standard: erlauben bis User entscheidet
    }
    const savedPrefs = localStorage.getItem('cookiePreferences');
    if (savedPrefs) {
      try {
        const prefs = JSON.parse(savedPrefs);
        return prefs.functional === true;
      } catch (e) {
        return true;
      }
    }
    return true;
  };

  return (
    <CookieManagerContext.Provider value={{ openCookieSettings, areFunctionalCookiesAllowed }}>
      {children}
      <CookieManagerComponent
        showBanner={showBanner}
        showModal={showModal}
        setShowBanner={setShowBanner}
        setShowModal={setShowModal}
        preferences={preferences}
        handleAcceptAll={handleAcceptAll}
        handleRejectAll={handleRejectAll}
        handleSavePreferences={handleSavePreferences}
        handleFunctionalToggle={handleFunctionalToggle}
      />
    </CookieManagerContext.Provider>
  );
};

// Separate Component für Cookie UI
const CookieManagerComponent: React.FC<{
  showBanner: boolean;
  showModal: boolean;
  setShowBanner: (show: boolean) => void;
  setShowModal: (show: boolean) => void;
  preferences: CookiePreferences;
  handleAcceptAll: () => void;
  handleRejectAll: () => void;
  handleSavePreferences: () => void;
  handleFunctionalToggle: (checked: boolean) => void;
}> = ({
  showBanner,
  showModal,
  setShowBanner,
  setShowModal,
  preferences,
  handleAcceptAll,
  handleRejectAll,
  handleSavePreferences,
  handleFunctionalToggle
}) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const modalRef = useRef<HTMLDivElement>(null);
  const [showTextToSpeech, setShowTextToSpeech] = useState(false);
  
  // Helper function to close modal and stop text-to-speech
  const closeModalAndStopSpeech = React.useCallback(() => {
    setShowModal(false);
    setShowTextToSpeech(false);
    window.speechSynthesis.cancel();
  }, [setShowModal]);
  
  // Language state
  const [language, setLanguage] = useState<'de' | 'en'>(() => {
    const stored = localStorage.getItem('siteLanguage');
    return (stored === 'en' || stored === 'de') ? stored : 'de';
  });
  
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent | StorageEvent) => {
      const newLang = e instanceof CustomEvent ? e.detail : localStorage.getItem('siteLanguage');
      if (newLang === 'en' || newLang === 'de') {
        setLanguage(newLang);
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    window.addEventListener('storage', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
      window.removeEventListener('storage', handleLanguageChange as EventListener);
    };
  }, []);
  
  const texts = cookieManagerData[language];
  
  const [expandedCategories, setExpandedCategories] = useState<{
    essential: boolean;
    functional: boolean;
  }>({
    essential: true, // Standard: geöffnet
    functional: true
  });

  const toggleCategory = (category: 'essential' | 'functional') => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  const [cookieMetadata, setCookieMetadata] = useState<CookieConsentData | null>(null);

  // Lade Cookie-Metadaten
  useEffect(() => {
    const saved = localStorage.getItem('cookieMetadata');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setCookieMetadata(data);
      } catch (e) {
        console.error('Failed to parse cookie metadata:', e);
      }
    }
  }, [showModal]);

  // Stop text-to-speech when modal closes
  useEffect(() => {
    if (!showModal && showTextToSpeech) {
      setShowTextToSpeech(false);
      window.speechSynthesis.cancel();
    }
  }, [showModal, showTextToSpeech]);

  // Helper-Funktionen
  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntilExpiration = (expiresAt: string): number => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getCookieStatus = (expiresAt: string): 'active' | 'expiring' | 'expired' => {
    const daysLeft = getDaysUntilExpiration(expiresAt);
    if (daysLeft < 0) return 'expired';
    if (daysLeft < 30) return 'expiring';
    return 'active';
  };

  // Get all focusable elements within the modal
  const getFocusableElements = React.useCallback(() => {
    if (!modalRef.current) return [];
    
    const focusableSelectors = [
      'input:not([disabled]):not([type="hidden"])',
      'button:not([disabled])',
      'a[href]',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    return Array.from(
      modalRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
    ).filter(el => {
      const style = window.getComputedStyle(el);
      // Allow elements with opacity 0 if they are inputs (for toggle switches)
      const isInput = el.tagName === 'INPUT';
      const isVisible = style.display !== 'none' && style.visibility !== 'hidden';
      const hasOpacity = isInput || style.opacity !== '0';
      return isVisible && hasOpacity;
    });
  }, []);

  // ESC-Taste zum Schließen des Modals und Banners + Focus Trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Wenn Text-to-Speech Player aktiv ist, nicht eingreifen (Player hat Priorität)
      if (showTextToSpeech && e.key === 'Tab') {
        const playerContainer = document.querySelector('[data-text-to-speech-player]');
        const activeElement = document.activeElement as HTMLElement;
        // Wenn Player aktiv ist, lasse den Player-Focus Trap übernehmen
        if (playerContainer) {
          // Prüfe ob Focus im Player ist oder ob Player aktiv ist
          if (playerContainer.contains(activeElement) || showTextToSpeech) {
            // Player-Focus Trap übernimmt - nicht eingreifen
            return;
          }
        }
      }

      // Focus Trap für Modal (nur wenn Player nicht aktiv)
      if (showModal && modalRef.current && e.key === 'Tab' && !showTextToSpeech) {
        const focusableElements = getFocusableElements();
        
        if (focusableElements.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement as HTMLElement;

        const isWithinModal = modalRef.current.contains(activeElement);

        if (!isWithinModal) {
          e.preventDefault();
          firstElement.focus();
          return;
        }

        if (e.shiftKey) {
          if (activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
        return;
      }

      // ESC-Taste zum Schließen
      if (e.key === 'Escape') {
        if (showModal) {
          e.preventDefault();
          // Remove focus before closing
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          closeModalAndStopSpeech();
        } else if (showBanner) {
          e.preventDefault();
          // Remove focus before closing
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          // ESC auf Banner: Alle akzeptieren (Standard-Verhalten)
          handleAcceptAll();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, showBanner, showTextToSpeech, handleAcceptAll, closeModalAndStopSpeech, getFocusableElements]);

  // Berechne Statistiken
  const stats = React.useMemo(() => {
    if (!cookieMetadata) return null;
    
    // Erweitere die Cookie-Liste um cookieConsent, falls es fehlt
    let allCookies = [...cookieMetadata.cookies];
    const hasCookieConsent = allCookies.some(c => c.name === 'cookieConsent');
    
    if (!hasCookieConsent && localStorage.getItem('cookieConsent')) {
      // cookieConsent existiert, aber ist nicht in Metadaten - füge es hinzu
      const consentDate = cookieMetadata.consentDate || new Date().toISOString();
      allCookies.push({
        name: 'cookieConsent',
        category: 'essential',
        description: 'Speichert, dass Sie eine Cookie-Entscheidung getroffen haben',
        expirationDays: 365,
        lastAccepted: consentDate,
        expiresAt: new Date(new Date(consentDate).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString()
      });
    }
    
    const activeCookies = allCookies.filter(c => {
      if (!c.expiresAt) return false;
      return getDaysUntilExpiration(c.expiresAt) > 0;
    }).length;
    
    const expiringCookies = allCookies.filter(c => {
      if (!c.expiresAt) return false;
      const days = getDaysUntilExpiration(c.expiresAt);
      return days > 0 && days < 30;
    }).length;

    return {
      total: allCookies.length,
      active: activeCookies,
      expiring: expiringCookies,
      consentDate: cookieMetadata.consentDate
    };
  }, [cookieMetadata]);

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <S.CookieBanner
            colors={colors}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => {
              // Klick außerhalb schließt nicht - User muss explizit wählen
            }}
          >
            <S.CookieBannerContent
              colors={colors}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <S.CookieBannerTitle colors={colors}>
                {texts.banner.title}
              </S.CookieBannerTitle>
              <S.CookieBannerText colors={colors}>
                {texts.banner.text}{' '}
                <a href="/dsgvo" target="_blank">{texts.banner.learnMore}</a>
              </S.CookieBannerText>
              <S.CookieBannerButtons>
                <S.CookieButton
                  colors={colors}
                  variant="secondary"
                  onClick={() => {
                    setShowBanner(false);
                    setShowModal(true);
                  }}
                >
                  {texts.banner.settings}
                </S.CookieButton>
                <S.CookieButton
                  colors={colors}
                  variant="secondary"
                  onClick={handleRejectAll}
                >
                  {texts.banner.reject}
                </S.CookieButton>
                <S.CookieButton
                  colors={colors}
                  variant="primary"
                  onClick={handleAcceptAll}
                >
                  {texts.banner.acceptAll}
                </S.CookieButton>
              </S.CookieBannerButtons>
            </S.CookieBannerContent>
          </S.CookieBanner>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showModal && (
          <S.CookieModal
            colors={colors}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModalAndStopSpeech}
          >
            <S.CookieModalContent
              ref={modalRef}
              colors={colors}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <S.PanelHeader colors={colors}>
                <S.HeaderTitle>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"/>
                  </svg>
                  {texts.modal.title}
                </S.HeaderTitle>
                <S.CloseButton onClick={closeModalAndStopSpeech} aria-label="Schließen">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </S.CloseButton>
              </S.PanelHeader>
              <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(90vh - 80px)' }}>
                <div style={{ marginBottom: '20px', padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 'clamp(0.9rem, 2vw, 0.85rem)', color: '#6b7280', flex: 1, minWidth: '200px' }}>
                    {texts.banner.readAloudDescription}
                  </div>
                  <button
                    onClick={() => setShowTextToSpeech(true)}
                    style={{
                      fontSize: 'clamp(1rem, 2.5vw, 0.9rem)',
                      padding: '0.6rem 1.2rem',
                      flexShrink: 0,
                      background: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = colors.primaryHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = colors.primary;
                    }}
                  >
                    {texts.banner.readAloud}
                  </button>
                </div>

              {/* Cookie-Statistiken */}
              {stats && (
                <S.CookieStats colors={colors}>
                  <h4>{texts.modal.stats.overview}</h4>
                  <S.StatsGrid>
                    <S.StatItem colors={colors}>
                      <p className="stat-value">{stats.total}</p>
                      <p className="stat-label">{texts.modal.stats.total}</p>
                    </S.StatItem>
                    <S.StatItem colors={colors}>
                      <p className="stat-value">{stats.active}</p>
                      <p className="stat-label">{texts.modal.stats.active}</p>
                    </S.StatItem>
                    {stats.expiring > 0 && (
                      <S.StatItem colors={colors}>
                        <p className="stat-value" style={{ color: colors.primaryHover }}>{stats.expiring}</p>
                        <p className="stat-label">{texts.modal.stats.expiring}</p>
                      </S.StatItem>
                    )}
                  </S.StatsGrid>
                  {stats.consentDate && (
                    <p style={{ marginTop: '10px', textAlign: 'center' }}>
                      <strong>{texts.modal.stats.consentGiven}</strong> {formatDate(stats.consentDate)}
                    </p>
                  )}
                </S.CookieStats>
              )}

              <S.CookieCategory colors={colors}>
                <S.CookieCategoryHeader 
                  isOpen={expandedCategories.essential}
                  onClick={() => toggleCategory('essential')}
                >
                  <div>
                    <S.CookieCategoryTitle colors={colors}>
                      {texts.modal.categories.essential.title}
                    </S.CookieCategoryTitle>
                    {expandedCategories.essential && (
                      <S.CookieCategoryDescription colors={colors}>
                        {texts.modal.categories.essential.description}
                      </S.CookieCategoryDescription>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <S.ToggleSwitch colors={colors} disabled onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={true} 
                        readOnly 
                        tabIndex={-1}
                        aria-label={`${texts.modal.categories.essential.title} aktiviert`}
                      />
                      <span />
                    </S.ToggleSwitch>
                    <S.CategoryToggleIcon 
                      isOpen={expandedCategories.essential} 
                      colors={colors}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategory('essential');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleCategory('essential');
                        }
                      }}
                      aria-label={`${expandedCategories.essential ? 'Zuklappen' : 'Aufklappen'}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </S.CategoryToggleIcon>
                  </div>
                </S.CookieCategoryHeader>
                <S.CookieCategoryContent
                  initial={false}
                  animate={{
                    height: expandedCategories.essential ? 'auto' : 0,
                    opacity: expandedCategories.essential ? 1 : 0
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  {expandedCategories.essential && (
                    <div style={{ marginTop: '10px' }}>
                      <S.CookieList colors={colors}>
                        {(() => {
                          // Hole alle essentiellen Cookies aus Metadaten
                          const essentialCookies = cookieMetadata?.cookies.filter(c => c.category === 'essential') || [];
                          
                          // Prüfe ob cookieConsent bereits in den Metadaten ist
                          const hasCookieConsent = essentialCookies.some(c => c.name === 'cookieConsent');
                          
                          // Wenn cookieConsent fehlt, füge es hinzu (für bestehende Nutzer)
                          const allEssentialCookies = [...essentialCookies];
                          if (!hasCookieConsent && localStorage.getItem('cookieConsent')) {
                            // cookieConsent existiert, aber ist nicht in Metadaten - füge es hinzu
                            allEssentialCookies.push({
                              name: 'cookieConsent',
                              category: 'essential',
                              description: texts.cookies.cookieConsent.description,
                              expirationDays: 365,
                              lastAccepted: cookieMetadata?.consentDate || new Date().toISOString(),
                              expiresAt: cookieMetadata?.consentDate 
                                ? new Date(new Date(cookieMetadata.consentDate).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString()
                                : new Date(new Date().getTime() + 365 * 24 * 60 * 60 * 1000).toISOString()
                            });
                          }
                          
                          // Wenn keine Metadaten vorhanden, zeige Fallback-Liste
                          if (allEssentialCookies.length === 0) {
                            return (
                              <>
                                <li><code>cookieConsent</code> - {texts.cookies.cookieConsent.description}</li>
                                <li><code>theme</code> - {texts.cookies.theme.description}</li>
                              </>
                            );
                          }
                          
                          // Zeige alle essentiellen Cookies mit Metadaten
                          return allEssentialCookies.map((cookie, idx) => {
                            const daysLeft = cookie.expiresAt ? getDaysUntilExpiration(cookie.expiresAt) : null;
                            const status = cookie.expiresAt ? getCookieStatus(cookie.expiresAt) : 'active';
                            // Verwende Übersetzung aus JSON wenn verfügbar
                            const cookieText = (texts.cookies as any)[cookie.name]?.description || cookie.description;
                            
                            return (
                              <React.Fragment key={idx}>
                                <li>
                                  <code>{cookie.name}</code> - {cookieText}
                                </li>
                                {cookie.expiresAt && (
                                  <>
                                    <p style={{ marginTop: '8px', marginBottom: '4px', fontSize: 'clamp(0.75rem, 1vw, 0.85rem)', color: colors.textSecondary }}>
                                      <strong>{texts.modal.cookieDetails.expiresAt}</strong> {formatDate(cookie.expiresAt)}
                                      {daysLeft !== null && daysLeft > 0 && ` (in ${daysLeft} ${daysLeft === 1 ? texts.modal.cookieDetails.daysLeft.singular : texts.modal.cookieDetails.daysLeft.plural})`}
                                      {daysLeft !== null && daysLeft <= 0 && ` (${texts.modal.cookieDetails.daysLeft.expired})`}
                                    </p>
                                    <p style={{ marginTop: '4px', marginBottom: '4px', fontSize: 'clamp(0.75rem, 1vw, 0.85rem)', color: colors.textSecondary }}>
                                      {status === 'active' ? texts.modal.cookieDetails.status.active : status === 'expiring' ? texts.modal.cookieDetails.status.expiring : texts.modal.cookieDetails.status.expired}
                                    </p>
                                    {cookie.lastAccepted && (
                                      <p style={{ marginTop: '4px', marginBottom: '4px', fontSize: 'clamp(0.75rem, 1vw, 0.85rem)', color: colors.textSecondary }}>
                                        <strong>{texts.modal.cookieDetails.accepted}</strong> {formatDate(cookie.lastAccepted)}
                                      </p>
                                    )}
                                    {/* Pause zwischen Cookies */}
                                    {idx < allEssentialCookies.length - 1 && (
                                      <p style={{ marginTop: '12px', marginBottom: '0', height: '0', visibility: 'hidden' }} aria-hidden="true">.</p>
                                    )}
                                  </>
                                )}
                              </React.Fragment>
                            );
                          });
                        })()}
                      </S.CookieList>
                    </div>
                  )}
                </S.CookieCategoryContent>
              </S.CookieCategory>

              <S.CookieCategory colors={colors}>
                <S.CookieCategoryHeader 
                  isOpen={expandedCategories.functional}
                  onClick={() => toggleCategory('functional')}
                >
                  <div>
                    <S.CookieCategoryTitle colors={colors}>
                      {texts.modal.categories.functional.title}
                    </S.CookieCategoryTitle>
                    {expandedCategories.functional && (
                      <S.CookieCategoryDescription colors={colors}>
                        {texts.modal.categories.functional.description}
                      </S.CookieCategoryDescription>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <S.ToggleSwitch colors={colors} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleFunctionalToggle(e.target.checked);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFunctionalToggle(!preferences.functional);
                          }
                        }}
                        aria-label={`${texts.modal.categories.functional.title} ${preferences.functional ? 'deaktivieren' : 'aktivieren'}`}
                      />
                      <span />
                    </S.ToggleSwitch>
                    <S.CategoryToggleIcon 
                      isOpen={expandedCategories.functional} 
                      colors={colors}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategory('functional');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleCategory('functional');
                        }
                      }}
                      aria-label={`${expandedCategories.functional ? 'Zuklappen' : 'Aufklappen'}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </S.CategoryToggleIcon>
                  </div>
                </S.CookieCategoryHeader>
                <S.CookieCategoryContent
                  initial={false}
                  animate={{
                    height: expandedCategories.functional ? 'auto' : 0,
                    opacity: expandedCategories.functional ? 1 : 0
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  {expandedCategories.functional && (
                    <div style={{ marginTop: '10px' }}>
                      <S.CookieList colors={colors}>
                        {cookieMetadata?.cookies
                          .filter(c => c.category === 'functional')
                          .map((cookie, idx, arr) => {
                            const daysLeft = cookie.expiresAt ? getDaysUntilExpiration(cookie.expiresAt) : null;
                            const status = cookie.expiresAt ? getCookieStatus(cookie.expiresAt) : 'active';
                            // Verwende Übersetzung aus JSON wenn verfügbar
                            const cookieText = (texts.cookies as any)[cookie.name]?.description || cookie.description;
                            
                            return (
                              <React.Fragment key={idx}>
                                <li>
                                  <code>{cookie.name}</code> - {cookieText}
                                </li>
                                {cookie.expiresAt && (
                                  <>
                                    <p style={{ marginTop: '8px', marginBottom: '4px', fontSize: 'clamp(0.75rem, 1vw, 0.85rem)', color: colors.textSecondary }}>
                                      <strong>{texts.modal.cookieDetails.expiresAt}</strong> {formatDate(cookie.expiresAt)}
                                      {daysLeft !== null && daysLeft > 0 && ` (in ${daysLeft} ${daysLeft === 1 ? texts.modal.cookieDetails.daysLeft.singular : texts.modal.cookieDetails.daysLeft.plural})`}
                                      {daysLeft !== null && daysLeft <= 0 && ` (${texts.modal.cookieDetails.daysLeft.expired})`}
                                    </p>
                                    <p style={{ marginTop: '4px', marginBottom: '4px', fontSize: 'clamp(0.75rem, 1vw, 0.85rem)', color: colors.textSecondary }}>
                                      {status === 'active' ? texts.modal.cookieDetails.status.active : status === 'expiring' ? texts.modal.cookieDetails.status.expiring : texts.modal.cookieDetails.status.expired}
                                    </p>
                                    {cookie.lastAccepted && (
                                      <p style={{ marginTop: '4px', marginBottom: '4px', fontSize: 'clamp(0.75rem, 1vw, 0.85rem)', color: colors.textSecondary }}>
                                        <strong>{texts.modal.cookieDetails.accepted}</strong> {formatDate(cookie.lastAccepted)}
                                      </p>
                                    )}
                                    {/* Pause zwischen Cookies */}
                                    {idx < arr.length - 1 && (
                                      <p style={{ marginTop: '12px', marginBottom: '0', height: '0', visibility: 'hidden' }} aria-hidden="true">.</p>
                                    )}
                                  </>
                                )}
                              </React.Fragment>
                            );
                          })}
                        {(!cookieMetadata || cookieMetadata.cookies.filter(c => c.category === 'functional').length === 0) && (
                          <>
                            <li><code>siteLanguage</code> - {texts.cookies.siteLanguage.description}</li>
                            <li><code>accessibilitySettings</code> - {texts.cookies.accessibilitySettings.description}</li>
                          </>
                        )}
                      </S.CookieList>
                    </div>
                  )}
                </S.CookieCategoryContent>
              </S.CookieCategory>

              <S.CookieModalButtons>
                <S.CookieButton
                  colors={colors}
                  variant="secondary"
                  onClick={() => {
                    closeModalAndStopSpeech();
                    if (!localStorage.getItem('cookieConsent')) {
                      setShowBanner(true);
                    }
                  }}
                >
                  {texts.modal.buttons.cancel}
                </S.CookieButton>
                <S.CookieButton
                  colors={colors}
                  variant="primary"
                  onClick={handleSavePreferences}
                >
                  {texts.modal.buttons.save}
                </S.CookieButton>
              </S.CookieModalButtons>
              </div>
            </S.CookieModalContent>
          </S.CookieModal>
        )}
      </AnimatePresence>

      {/* Text-to-Speech Player für Cookie-Popup */}
      <TextToSpeechPlayer
        isActive={showTextToSpeech}
        onClose={() => setShowTextToSpeech(false)}
        targetElement={modalRef.current}
      />
    </>
  );
};

export default CookieManagerProvider;
